import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { LinkOutlined } from '@ant-design/icons';
import { Button, Input, Modal, Steps } from 'antd';

import { EMPTY_ID } from '@totem/components/common/referencesModal/ReferencesModal';
import TicketTable from '@totem/components/services/threats/ticketLinkModal/TicketTable';
import TicketListContainer, {
  containerConfiguration as ticketContainerConfiguration,
} from '@totem/components/ticketing/listContainer/TicketListContainer';
import { TicketQueryResult } from '@totem/components/ticketing/ticketing';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import InputContainer from '@totem/components/ui_components/reportSettings/InputContainer';
import { Event } from '@totem/types/events';
import { isNotNull } from '@totem/utilities/common';

import '../events.css';

export interface Props {
  event: Event;
  visible: boolean;
  onClose: () => void;
  onAssociateTicket: (eventId: string, ticket: TicketQueryResult) => void;
}

const TicketAssociateModal = ({
  event,
  visible,
  onClose,
  onAssociateTicket,
}: Props) => {
  const [selectedTicket, setSelectedTicket] = useState<TicketQueryResult>(null);
  const [searchTicketNumber, setSearchTicketNumber] = useState<string>('');
  const handleTicketSelected = (
    ticketId: string,
    ticket: TicketQueryResult,
  ) => {
    setSelectedTicket(ticket);
  };

  const getTicketStatus = () => {
    if (typeof selectedTicket !== 'undefined' && selectedTicket !== null) {
      return 'finish';
    }

    return 'process';
  };

  const getTicketTitle = () => {
    if (typeof selectedTicket !== 'undefined' && selectedTicket !== null) {
      return 'Ticket';
    }
    return 'Select Ticket (use ticket table below)';
  };

  const getTicketContent = () => {
    if (typeof selectedTicket !== 'undefined' && selectedTicket !== null) {
      return (
        <span>
          <Link
            to={`/dashboard/ticket/${selectedTicket.ticket.id}`}
            target="eventTicketDetails"
          >
            {selectedTicket.ticket.number}
          </Link>
          : {selectedTicket.ticket.summary}
        </span>
      );
    }
    return '';
  };

  const getAssociationStatus = () => {
    if (typeof selectedTicket !== 'undefined' && selectedTicket !== null) {
      return 'process';
    }

    return 'wait';
  };

  const handleCreateAssociation = () => {
    if (typeof onAssociateTicket === 'function') {
      onAssociateTicket(event.id, selectedTicket);
    }
  };

  const handleSearch = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = evt.target.value;
    if (searchTerm !== null) {
      setSearchTicketNumber(searchTerm);
    } else {
      setSearchTicketNumber('');
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      title={<ModalTitle>Associate Event to Ticket</ModalTitle>}
      confirmLoading={false}
      width={'60%'}
      footer={
        <ModalFooter>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      }
    >
      <Steps
        orientation="vertical"
        current={1}
        items={[
          {
            title: 'Event',
            status: 'finish',
            content: event.description,
          },
          {
            title: getTicketTitle(),
            status: getTicketStatus(),
            content: getTicketContent(),
          },
          {
            title: 'Create Association',
            status: getAssociationStatus(),
            content: (
              <Button
                type="primary"
                shape="round"
                icon={<LinkOutlined />}
                onClick={handleCreateAssociation}
                disabled={getAssociationStatus() === 'wait'}
              >
                Associate
              </Button>
            ),
          },
        ]}
      />
      <br />
      <Input
        placeholder="Filter by Ticket Number"
        style={{ width: '50%', maxWidth: '150px' }}
        onChange={handleSearch}
      />
      <InputContainer
        filterConfig={ticketContainerConfiguration}
        defaultSort={{
          field: 'number',
          direction: 'descend',
        }}
        staticFilters={[
          {
            field: 'number',
            operator: 'cscontains',
            values:
              isNotNull(searchTicketNumber) && searchTicketNumber !== ''
                ? [searchTicketNumber]
                : [],
          },
        ]}
        defaultFilters={[
          {
            field: 'status',
            values: ['OPEN', 'RESOLVED'],
          },
        ]}
      >
        <TicketListContainer onTicketSelected={handleTicketSelected}>
          <div styleName="card-body">
            <TicketTable
              selectedTicketId={
                typeof selectedTicket !== 'undefined' && selectedTicket !== null
                  ? selectedTicket.ticket.id
                  : EMPTY_ID
              }
            />
          </div>
        </TicketListContainer>
      </InputContainer>
    </Modal>
  );
};

export default TicketAssociateModal;
